import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
    NavigationControl, 
    Source,
    Popup,
    Layer 
} from 'react-map-gl/maplibre';
import { Box, Grid, MenuItem, Select, Switch, Tooltip as MTooltip } from '@mui/material';
import { Edit, EditSquare, Flight, Settings, Terrain, Visibility, Wifi, WifiOff } from '@mui/icons-material';
import { bearingBetween, offsetPosition, zoomFromAltitude, pitchFromSlope, trackData } from './simHelper';
import { useUser } from '../../store/User';
import 'maplibre-gl/dist/maplibre-gl.css';
import terrainData from './terrain.json';
import darkMap from './custom-dark.json';
import offlineMap from './mlt.json';
import { apiUrl } from '../../api';
import { DeckGL } from '@deck.gl/react';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PathLayer } from '@deck.gl/layers';
import { getRectangle, getBounds, terraBaseOptions } from '../Util';
import { AOIContainer } from './aoiContainer';

import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css'; 
import './index.css';

const pmtilesProtocol = new Protocol();
maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);

const EMPTY = {"FeatureCollection": []};


const Libre = ({ sidebar }) => {
    const map = useRef();
    const track = useRef(null);
    const hoveredId = useRef(null);
    const isDragging = useRef(false);

    const { aoi, aois, imagery, location, mapConfig, terraBase, terraBases, tacticalTerraBase, user, worldTerraBase, 
            setAOI, setMapConfig, setShowLogin, setTerraBase, setUser } = useUser();

    const savedView = JSON.parse(localStorage.getItem('terra-view'));

    const [dragIndex, setDragIndex] = useState(null);
    const [mgrsGrids, setMgrsGrids] = useState(EMPTY);
    const [mgrsId, setMgrsId] = useState(null);
    const [offline, setOffline] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [shiftDown, setShiftDown] = useState(false);
    const [session, setSession] = useState(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            if (e.key === "Backspace" || e.key === "Delete") {
                setAOI(prev => {
                    if(prev?.bounds?.coordinates && prev.bounds.coordinates[0].length > 1) {
                        const newCoords = prev.bounds.coordinates[0].slice(0, -1);
                        return {
                            ...prev,
                            bounds: {
                            ...prev.bounds,
                            coordinates: [newCoords]
                            }
                        };
                    } else {
                        return null;
                    }
                });
            }

            // if (e.key === "Shift") setShiftDown(true);
        };

        const handleKeyUp = (e) => {
            if (e.key === "Shift") setShiftDown(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keydown", handleKeyDown);
        };

        // setMgrsGrids(mgrsGridData);
    }, []);

    useEffect(() => {
        console.log(`${mapConfig.drawPolygons}-${user?.id}-session`);
        setSession(`${mapConfig.drawPolygons}-${user?.id}-session`);
    }, [mapConfig]);


    // useEffect(() => {
    //     if (!map.current) return;

    //     const maplibre = map.current.getMap();

    //     const overlay = new MapboxOverlay({
    //     interleaved: true,
    //     layers: [
    //         new PathLayer({
    //         id: "flight-path",
    //         data: [trackData],
    //         getPath: d => d.geometry.coordinates,
    //         getColor: [26, 163, 74],
    //         getWidth: 5,
    //         widthUnits: "pixels",
    //         extruded: true,
    //         getElevation: p => p[2]
    //         })
    //     ]
    //     });

    //     track.current = overlay;
    //     maplibre.addControl(overlay);
    // }, [map?.current])

    useEffect(() => {
        if(map?.current && mapConfig.drawPolygons){
            const m = map.current.getMap();
            m.setPitch(0);
            m.setBearing(0);
        }
    }, [mapConfig.drawPolygons]);

    useEffect(() => {
        if(map?.current && aoi && aoi?.name){
            flyToAOI();
        }
    }, [aoi]);

    useEffect(() => {
        if (!map?.current) return;
        
        const rawMap = map.current.getMap(); 
        if (!rawMap) return;

        if (user && user?.role?.includes('sales')) {
            rawMap.setMaxBounds([-200, -90, 200, 90]);
            if (savedView?.zoom && savedView.zoom > 11) {
                rawMap.setZoom(10);
                rawMap.setMaxZoom(11);
            }
        } else {
            rawMap.setMaxBounds(null);
            rawMap.setMaxZoom(24);
        }

    }, [user]);

    useEffect(() => {
        if(map?.current && location && location.length === 2) {
            const m = map.current.getMap();
            m.flyTo({
                center: location,
                bearing: 0,
                pitch: 0,
                zoom: 9,
                duration: 2000,
                essential: true
            });
        }
    }, [location]);

    const getCenter = (geometry) => {
        if (!geometry || !geometry.coordinates) return;

        // Flatten coordinates to a single array of [lng, lat] points
        const points = geometry.type === "MultiPolygon" 
            ? geometry.coordinates.flat(2) 
            : geometry.coordinates.flat(1);

        // Safeguard against empty or incomplete geometries
        if (points.length === 0) return;

        const lngs = points.map(c => c[0]);
        const lats = points.map(c => c[1]);

        return [
            (Math.min(...lngs) + Math.max(...lngs)) / 2,
            (Math.min(...lats) + Math.max(...lats)) / 2
        ];
    };

    const flyToAOI = () => {
        const m = map.current.getMap();
        const coords = aoi.bounds.coordinates[0];

        const bounds = getBounds(coords);

        m.fitBounds(bounds, { bearing: 0, pitch: 0, padding: 150, duration: 2000 });
    }

    const onMouseDown = (e) => {
        isDragging.current = false;  // reset

        const m = map.current?.getMap();
        if (!m) return;

        const feature = e.features?.find(f => f.layer.id === "temp-points-layer");
        if (!feature) return;

        setDragIndex(feature.properties.index);

        m.dragPan.disable();
        m.dragRotate.disable();
        m.scrollZoom.disable();
        m.boxZoom.disable();
        m.doubleClickZoom.disable();
        m.touchZoomRotate.disable();

        m.getCanvas().style.cursor = "grabbing";
    };

    const onMouseUp = () => {
        const m = map.current?.getMap();
        if (!m) return;

        setDragIndex(null);

        m.dragPan.enable();
        m.dragRotate.enable();
        m.scrollZoom.enable();
        m.boxZoom.enable();
        m.doubleClickZoom.enable();
        m.touchZoomRotate.enable();

        m.getCanvas().style.cursor = "";
    };

    const onMouseMove = (e) => {
        const m = map.current?.getMap();
        if (!m) return;

        // Handle circle drag       
        if (dragIndex !== null) {
            isDragging.current = true; 
            const { lng, lat } = e.lngLat;

            setAOI(prev => {
                const newCoords = [...prev.bounds.coordinates[0]];
                newCoords[dragIndex] = [lng, lat];

                return {
                    ...prev,
                    bounds: {
                        ...prev.bounds,
                        coordinates: [newCoords]  // new array reference
                    }
                };
            });

            return;
        }

        if(mapConfig.drawPolygons && aoi && shiftDown) {
            setAOI({
                ...aoi,
                bounds: { ...aoi.bounds, coordinates: [getRectangle(aoi.bounds.coordinates[0][0], [e.lngLat.lng, e.lngLat.lat])] }
            });
        }


        const SRC = { source: "mgrs-pmtiles-source", sourceLayer: "mgrs_data" };
        const mfeature = e.features?.find(f => f.layer.id === "mgrs-fill-layer");

        if (!mfeature || mfeature.id == null) {
            if (hoveredId.current !== null) {
                m.setFeatureState({ ...SRC, id: hoveredId.current }, { hover: false });
                hoveredId.current = null;
            }
            return;
        }

        if (hoveredId.current === mfeature.id) return;

        if (hoveredId.current !== null) {
            m.setFeatureState({ ...SRC, id: hoveredId.current }, { hover: false });
        }


        hoveredId.current = mfeature.id;
        m.setFeatureState({ ...SRC, id: mfeature.id }, { hover: true });

        const centerArray = getCenter(mfeature?.geometry);
        setMgrsId({ name: mfeature.properties.name, center: centerArray });

    };

    const onMoveEnd = useCallback((e) => {
        if (!map.current) return;
        const m = e.target;
        if(mapConfig.mgrs) {            
            const currentZoom = m.getZoom();
            if (currentZoom > 8) {
                // setMgrsGrids(mgrsGridData);
            } else {
                // setMgrsGrids(EMPTY);
                setMgrsId(null);
            }
        }

        const center = m.getCenter();
        const view = {
            lat: center.lat,
            lng: center.lng,
            bearing: m.getBearing(),
            pitch: m.getPitch(),
            zoom: m.getZoom(),
        };
        localStorage.setItem('terra-view', JSON.stringify(view));
    }, [mapConfig.mgrs]);

    const onMouseLeave = () => {
        const m = map.current?.getMap();
        if (m && map?.current.getSource('mgrs') && hoveredId.current !== null) {
            m.setFeatureState(
                { source: 'mgrs', id: hoveredId.current },
                { hover: false }
            );
            hoveredId.current = null;
        }
    };

    const handleMapClick = useCallback((e) => {
        console.log(mapConfig.drawPolygons);
        if (isDragging.current || !mapConfig.drawPolygons || dragIndex) return;
        const m = map.current.getMap();

        // Manual AOI polygon mode
        if (!mapConfig.mgrs) {
            const { lng, lat } = e.lngLat;

            setAOI(prev => {
                if (!prev) {
                    return {
                        bounds: {
                        type: "Polygon",
                        coordinates: [[[lng, lat]]] 
                        }
                    };
                }
                return {
                    ...prev,
                    bounds: {
                        type: "Polygon",
                        coordinates: [
                            [
                                ...prev.bounds.coordinates[0],
                                [lng, lat]
                            ]
                        ]
                    }
                };
            });

            return;
        }

        // MGRS grid mode
        const mgrsFeature = e.features?.find(f => f.layer.id === "mgrs-fill-layer");

        if (!mgrsFeature) return;

        const gridFeatures = m.queryRenderedFeatures(e.point, {
            layers: ["mgrs-fill-layer"]
        });

        const feature = gridFeatures[0];
        const ring = feature.geometry.coordinates[0];
        

        setAOI(prev => {
            if (!prev) {
                return {
                    bounds: {
                        type: "MultiPolygon",
                        coordinates: [[ring]]
                    }
                };
            }

            const coords = prev.bounds.coordinates;

            // Check if already selected
            const exists = coords.some(c => JSON.stringify(c[0]) === JSON.stringify(ring));

            if (exists) {
                if(coords[0].length === 1) {
                    return null;
                } else {
                    return {
                        ...prev,
                        bounds: {
                            type: "MultiPolygon",
                            coordinates: coords.filter(c => JSON.stringify(c[0]) !== JSON.stringify(ring))
                        }
                    };
                }
            }

            // Add it
            return {
                ...prev,
                bounds: {
                    type: "MultiPolygon",
                    coordinates: [...coords, [ring]]
                }
            };
        });
    }, [mapConfig.mgrs, mapConfig.drawPolygons]);

    const memoGrids = useMemo(() => {
        return mgrsGrids;
    }, [mgrsGrids]);

    function testFly() {
        const speed = 0.001;
        const coords = trackData.geometry.coordinates;
        let progress = 0;

        const maplibre = map.current.getMap();

        // store previous interpolated position
        let lastPos = coords[0];

        function animate() {
            progress += speed;
            if (progress >= 1) return;

            const idx = progress * (coords.length - 1);
            const i0 = Math.floor(idx);
            const i1 = Math.min(i0 + 1, coords.length - 1);

            const t = idx - i0;

            const [lng0, lat0, alt0] = coords[i0];
            const [lng1, lat1, alt1] = coords[i1];

            const lng = lng0 + (lng1 - lng0) * t;
            const lat = lat0 + (lat1 - lat0) * t;
            const alt = alt0 + (alt1 - alt0) * t;

            const currentPos = [lng, lat, alt];

            // bearing from interpolated movement
            const bearing = bearingBetween(lastPos, currentPos);

            // camera offset
            const [camLng, camLat] = offsetPosition(lng, lat, bearing, 40);

            // zoom from altitude
            const zoom = zoomFromAltitude(alt);

            // pitch from actual 3D movement
            const pitch = pitchFromSlope(alt0, alt1);
            // const pitch = pitchFrom3D(lastPos, currentPos);

            // update lastPos
            lastPos = currentPos;

            maplibre.jumpTo({
                center: [camLng, camLat],
                bearing,
                pitch,
                zoom
            });

            requestAnimationFrame(animate);
        }

        animate();
    }

    const getInitialViewState = () => {
        // const zoomValue = !user?.role?.includes('sales') || user?.role?.includes('sales') && savedView?.zoom < 15 && savedView?.zoom ? savedView.zoom : 8;
        const zoomValue = (!user?.role?.includes('sales') || (user?.role?.includes('sales') && savedView?.zoom < 15)) ? (savedView?.zoom ?? 8) : 8;

        return {
            // center: [savedView?.lng || mapConfig.position[1], savedView?.lat || mapConfig.position[0]],
            latitude: savedView?.lat || mapConfig.position[0],
            longitude: savedView?.lng || mapConfig.position[1],
            bearing: savedView?.bearing || 0,
            pitch: savedView?.pitch || 0,
            zoom: zoomValue,
        };
    };

    const onMapLoad = (evt) => {
        const mapInstance = evt.target;
        mapInstance.setMaxBounds([-200, -90, 200, 90]);
        mapInstance.setMaxZoom(11);
    };

    const mgrsPmtilesUrl = `${window.location.origin}/data/mgrs_10km.pmtiles`;
    // const recognitionPmtilesUrl = `${window.location.origin}/data/recognition.vector.pmtiles`;

    return (
        <div style={{ width: '100%', height: 'calc(100vh - 66px)', background: map?.current?.getMap().getZoom() > 8 ? '#7e7e7e' : "#000"}}>
            <Map
                ref={map}
                id="aoi-map"
                key={session}
                mapStyle={ darkMap }

                projection={"globe"} 
                maxPitch={85}
                terrain={{ source: 'terrainSource', exaggeration: 1 }}
                sky={{
                    'atmosphere-blend': [
                        'interpolate', ['linear'], ['zoom'], 
                        0, 1, // Full glow (atmosphere) at low zooms
                        5, 1, 
                        7, 0  // Fade out atmosphere as you zoom in
                    ]
                }}

                initialViewState={getInitialViewState()}

                interactiveLayerIds={["mgrs-fill-layer", "temp-points-layer", "temp-polygon-layer", "terrabase-polygon-3d"]}

                onClick={handleMapClick}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseLeave}
                onMouseMove={onMouseMove}
                onMoveEnd={onMoveEnd}
                attributionControl={false}
                worldCopyJump={false}
                onLoad={onMapLoad}
            >
                { mapConfig.terrain && (
                    <>
                        <Source 
                            id="terrainSource" 
                            type="raster-dem" 
                            {...terrainData}
                            tileSize={512}
                        >
                            <Layer
                                id="hills"
                                type="hillshade"
                                paint={{
                                'hillshade-exaggeration': 0.65,
                                'hillshade-highlight-color': '#696969',
                                'hillshade-shadow-color': '#1a1a1a'
                                }}
                            />
                        </Source>

                        <Source id="openfreemap" type="vector" url="https://tiles.openfreemap.org/planet">
                            <Layer
                                id="3d-buildings"
                                source-layer="building"
                                type="fill-extrusion"
                                minzoom={14}
                                paint={{
                                "fill-extrusion-color": "#a5a5a5",
                                "fill-extrusion-height": ["get", "render_height"],
                                "fill-extrusion-base": ["get", "render_min_height"],
                                "fill-extrusion-opacity": 0.95,
                                "fill-extrusion-vertical-gradient": true
                                }}
                            />
                        </Source>
                    </>
                )}

                <Source
                    id="mgrs-pmtiles-source"
                    type="vector"
                    url={`pmtiles://${mgrsPmtilesUrl}`}
                    promoteId={{ mgrs_data: "name" }}
                >
                    <Layer
                        id="mgrs-fill-layer"
                        type="fill"
                        source-layer="mgrs_data"
                        minZoom={9}
                        paint={{
                            "fill-color": "#1aa34a",
                            "fill-opacity": [
                                "case",
                                ["==", ["get", "name"], mgrsId?.name ?? ""],
                                0.4,
                                0
                            ]
                        }}
                        layout={{
                            visibility: user?.role?.includes("sales") ? "visible" : "none"
                        }}
                    />
                    <Layer
                        id="outline-layer"
                        type="line"
                        source-layer="mgrs_data"
                        minZoom={9}
                        paint={{
                            "line-color": "#fff",
                            "line-opacity": 0.35,
                            "line-width": 2,
                            "line-dasharray": [2, 2]
                        }}
                        layout={{
                            visibility: user?.role?.includes("sales") ? "visible" : "none"
                        }}
                    />
                </Source>

                <Source
                    id="imagery-polygon"
                    type="geojson"
                    data={imagery ? imagery : {"type": "FeatureCollection", "features": []}}
                >
                    <Layer
                        id="imagery-polygon-layer"
                        type="fill"
                        paint={{ 
                            'fill-color': '#006AF9',
                            'fill-opacity': .45
                        }}
                        layout={{
                            visibility: mapConfig.imagery && imagery? "visible" : "none"
                        }}
                    />
                </Source>
                
                {/* For pmtiles as source */}
                {/* <Source
                    id="recognition-pmtiles-source"
                    type="vector"
                    url={`pmtiles://${recognitionPmtilesUrl}`}
                >
                    <Layer
                        id="recognition-tile-layer"
                        type="fill"
                        source-layer="occupancy"
                        paint={{
                            'fill-color': ['get', 'color'],
                            'fill-antialias': false,
                        }}
                    />
                </Source> */}
              
                
                {/* <Source id="mgrs" type="geojson" data={memoGrids} generateId={true} >
                    <Layer
                        id="mgrs-fill-layer"
                        type="fill"
                        source="mgrs"
                        minZoom={9}
                        paint={{
                            "fill-color": "#1aa34a",
                            "fill-opacity": [
                            "case",
                            ["boolean", ["feature-state", "hover"], false],
                            0.4,
                            0
                            ]
                        }}
                        layout={{
                            visibility: mapConfig.mgrs ? "visible" : "none"
                        }}
                    />
                    <Layer
                        id="outline-layer"
                        type="line"
                        source="mgrs"
                        minZoom={9}
                        paint={{
                            "line-color": "#fff",
                            "line-opacity": 0.5,
                            "line-width": 2,
                            "line-dasharray": [2, 2]
                        }}
                        layout={{
                            visibility: mapConfig.mgrs ? "visible" : "none"
                        }}
                    />
                </Source> */}

                <Source
                    id="temp-polygon"
                    type="geojson"
                    data={aoi?.bounds ?{ 
                        ...aoi.bounds
                    } : {}}
                >
                    <Layer
                        id="temp-polygon-layer"
                        type="fill"
                        paint={{ 
                            'fill-color': '#FF073A',
                            'fill-opacity': 0.35
                        }}
                        layout={{
                            visibility: mapConfig.drawPolygons && aoi ? "visible" : "none"
                        }}
                    />
                    <Layer 
                        id="temp-polygon-outline"
                        type="line" 
                        paint={{ 'line-color': '#FF073A', 'line-width': 2 }}
                        layout={{
                            visibility: mapConfig.drawPolygons && aoi ? "visible" : "none"
                        }}
                    />
                </Source>


                <Source 
                    id="custom-tile-source" 
                    type="raster" 
                    tiles={['/terrabase/mbtiles/{z}/{x}/{y}.png']} // Added "tile."
                    tileSize={256}
                >
                    <Layer
                        id="custom-tile-layer"
                        type="raster"
                        paint={{
                            'raster-opacity': 1.0,
                        }}
                        layout={{
                            visibility: mapConfig.occupancy ? "visible" : "none"
                        }}
                    />
                </Source>
   
                <Source id="terrabase-data" type="geojson" data={terraBase?.layer ? terraBase.layer : {}}>
                    <Layer
                        id="terrabase-polygon-3d"
                        type="fill-extrusion"
                        paint={{
                            "fill-extrusion-color": "#fff",
                            "fill-extrusion-height": ["get", "max_alt"],
                            "fill-extrusion-base": ["get", "min_alt"],
                            "fill-extrusion-opacity": 0.45
                        }}
                        layout={{
                            visibility: terraBase && terraBase?.layer ? "visible" : "none"
                        }}
                    />
                </Source>

                <Source id="tactical-data" type="geojson" data={tacticalTerraBase?.layer || { type: "FeatureCollection", features: [] }} buffer={512} tolerance={0.375}>
                    <Layer
                        id="tactical-polygon-3d"
                        type="fill-extrusion"
                        // beforeId="world-polygon-3d"
                        layout={{
                            visibility: mapConfig.tactical && tacticalTerraBase?.layer ? "visible" : "none"
                        }}
                        paint={{
                            "fill-extrusion-color": "#FFF01F",
                            "fill-extrusion-height": ["get", "max_alt"],
                            "fill-extrusion-base": ["get", "min_alt"],
                            "fill-extrusion-opacity": terraBase?.layer ? 0.15 : 0.35
                        }}
                    />
                </Source>
                

                <Source id="world-data" type="geojson" data={worldTerraBase?.layer || EMPTY}>
                    <Layer
                        id="world-polygon-3d"
                        type="fill-extrusion"
                        layout={{
                            visibility: mapConfig.world && worldTerraBase?.layer ? "visible" : "none"
                        }}
                        paint={{
                            "fill-extrusion-color": "#1aa34a",
                            "fill-extrusion-height": ["get", "max_alt"],
                            "fill-extrusion-base": ["get", "min_alt"],
                            "fill-extrusion-opacity": terraBase?.layer ? 0.15 : 0.35
                        }}
                    />
                </Source>
                
                <Source
                    id="my-polygon-data"
                    type="geojson"
                    data={{ 
                        type: 'FeatureCollection', 
                        features: aois.map(aoi => {
                        const ring = aoi.bounds.coordinates[0];
                        const isClosed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
                        const closedRing = isClosed ? ring : [...ring, ring[0]];

                        return { 
                            type: 'Feature', 
                            properties: { id: aoi.id, name: aoi.name, user: aoi.user }, // Keeps your metadata accessible to the layer
                            geometry: { 
                            type: 'Polygon', 
                            coordinates: [closedRing] // Nested inside a outer array
                            } 
                        };
                        }) 
                    }}
                >
                    <Layer
                        id="aoi-polygon-layer"
                        type="fill"
                        paint={{ 
                        'fill-color': '#fff',
                        'fill-opacity': 0.45
                        }}
                        layout={{
                            visibility: mapConfig.aois && aois && aois.length > 0 ? "visible" : "none"
                        }}
                    />
                    <Layer
                        id="aoi-polygon-outline"
                        type="line"
                        paint={{
                            "line-color": "#1aa34a",
                            "line-width": 2,
                            "line-opacity": 0.5
                            // "line-dasharray": [2, 2]
                        }}
                        layout={{
                            visibility: mapConfig.aois && aois && aois.length > 0 ? "visible" : "none"
                        }}
                    />
                </Source>

                <Source
                    id="temp-points"
                    type="geojson"
                    data={{
                        type: "FeatureCollection",
                        features: aoi?.bounds?.coordinates.length > 0 ? aoi.bounds.coordinates[0].map((coord, index) => ({
                        type: "Feature",
                        geometry: { type: "Point", coordinates: coord },
                        properties: { index }
                        })) : []}
                    }  
                >
                    <Layer
                        id="temp-points-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 6,
                            "circle-color": "#FF073A",
                            "circle-stroke-color": "white",
                            "circle-stroke-width": 2
                        }}
                        layout={{
                            visibility: mapConfig.drawPolygons && aoi ? "visible" : "none"
                        }}
                    />
                </Source>

                { mapConfig.aois && aois && aois.length > 0 &&
                    aois.filter((aoiObj) => aoiObj.id !== aoi?.id).map((aoiObj, index) => {
                        const center = getCenter(aoiObj.bounds);
                        return (
                            <Popup
                                key={`popup-aoi-${aoiObj.id || index}`}
                                longitude={center[0]}
                                latitude={center[1]}
                                closeButton={false}
                                closeOnClick={false}
                                anchor="bottom"
                                maxWidth="none"
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <div
                                            onClick={(e) => setAOI(aoiObj)}
                                            style={{ cursor: 'pointer', marginRight: '10px' }}
                                        >
                                            {aoiObj.name || `AOI ${index + 1}`}
                                        </div>
                                    </Box>
                                </Grid>
                            </Popup>
                        );
                    }
                )}

                { mapConfig.mgrs && mgrsId && (
                    <Popup
                        key={`popup-${mgrsId}`}
                        longitude={mgrsId.center[0]}
                        latitude={mgrsId.center[1]}
                        closeButton={false}
                        closeOnClick={false}
                        closeOnMove={false}
                        anchor="bottom"
                        className="mgrs-popup"
                    >
                        <div>{mgrsId.name}</div>
                    </Popup>
                )}

                { aoi && aoi?.bounds && aoi.bounds?.coordinates && (() => {
                    const coords = getCenter(aoi.bounds);
                    return (
                        <Popup
                            key={`popup-aoi-active`}
                            longitude={coords[0]}
                            latitude={coords[1]}
                            offset={aoi?.name ? [0, 0] : [0, 100]}
                            closeButton={false}
                            closeOnClick={false}
                            anchor="bottom"
                            maxWidth="300px"
                        >
                            <AOIContainer />
                        </Popup>
                    );
                })()}

                <NavigationControl visualizePitch={true} position="bottom-right" />
            </Map>
        </div>
    );
}

export default Libre;